
import React, { useMemo, useState } from 'react';
import { TrendingUp, DollarSign, ShieldCheck, AlertTriangle, ArrowUpRight, FileText, Download, Eye, BarChart3, TrendingDown, Activity, MessageSquare } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart as RePieChart, Pie, LineChart, Line, AreaChart, Area, Legend } from 'recharts';
import { useInvestorMetrics } from '../../hooks/queries/useInvestorMetrics';
import { useNavigate } from '../../routes';
import { useNavigateTo } from '@/utils/typeSafeNavigation';
import { formatCurrency, formatNumber, formatPercentage } from '@repo/shared-utils';
import { useCurrentInvestorId } from '../../hooks/queries/useEntityId';
import { chartColors, getChartTheme } from '@/utils/chartTheme';
import { getAppNow } from '@/utils/demoClock';

// InvestorDashboardView has no props

export const InvestorDashboardView: React.FC = () => {
  // Get investor ID from user account (with linking support)
  const { data: currentInvestorId } = useCurrentInvestorId();
  const [analyticsView, setAnalyticsView] = useState<'overview' | 'performance' | 'risk'>('overview');

  const { data: metricsData, isLoading } = useInvestorMetrics(currentInvestorId || undefined);

  // Extract metrics data with safe defaults - hooks must be called before early return
  const portfolio = React.useMemo(() => metricsData?.properties ?? [], [metricsData?.properties]);
  const entities = React.useMemo(() => metricsData?.entities ?? [], [metricsData?.entities]);
  const entityPerformance = React.useMemo(() => metricsData?.entityMetrics ?? [], [metricsData?.entityMetrics]);
  const metrics = React.useMemo(() => metricsData ? {
    totalCapitalInvested: metricsData.totalCapitalInvested,
    roi: metricsData.roi,
    avgOccupancy: metricsData.avgOccupancy,
    totalValuation: metricsData.totalValuation,
    totalNOI: metricsData.totalProRataNOI
  } : {
    totalCapitalInvested: 0,
    roi: 0,
    avgOccupancy: 0,
    totalValuation: 0,
    totalNOI: 0
  }, [metricsData]);
  const distributions = React.useMemo(() => metricsData ? {
    ytd: metricsData.distributionsYTD,
    byEntity: entityPerformance.map(ep => ({
      entityId: ep.entity.id,
      entityName: ep.entity.name,
      amount: ep.distributions
    })),
    history: []
  } : { ytd: 0, byEntity: [], history: [] }, [metricsData, entityPerformance]);

  // Chart Data preparation - Entity allocation instead of property allocation
  const allocationData = useMemo(() => {
    if (!entityPerformance || entityPerformance.length === 0) return [];
    return entityPerformance.map(ep => ({
      name: ep.entity.name,
      value: ep.capitalInvested
    }));
  }, [entityPerformance]);

  const COLORS = [chartColors.brand500, chartColors.emerald500, chartColors.amber500, chartColors.purple500, chartColors.pink500];
  const { gridColor, tooltipBg, tooltipColor, tooltipBorder } = getChartTheme(true);

  // Performance comparison data (portfolio vs benchmark)
  const performanceComparison = useMemo(() => {
    const months = [];
    const now = getAppNow();
    const benchmarkROI = 8; // Market benchmark ROI
    const roi = metrics?.roi ?? 0;

    const getMonthName = (date: Date) => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames[date.getMonth()];
    };

    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: getMonthName(date),
        // Use deterministic values based on month index instead of Math.random()
        portfolio: roi + ((i * 7) % 20 - 10) / 10,
        benchmark: benchmarkROI + ((i * 11) % 10 - 5) / 10
      });
    }
    return months;
  }, [metrics?.roi]);

  // ROI scenarios
  const roiScenarios = useMemo(() => {
    const baseROI = metrics?.roi ?? 0;
    return [
      { scenario: 'Conservative', roi: baseROI * 0.8, probability: 20 },
      { scenario: 'Base Case', roi: baseROI, probability: 60 },
      { scenario: 'Optimistic', roi: baseROI * 1.2, probability: 20 }
    ];
  }, [metrics?.roi]);

  // Risk metrics
  const riskMetrics = useMemo(() => {
    if (!portfolio || portfolio.length === 0 || !metrics) {
      return {
        concentrationRisk: 'Low' as const,
        occupancyRisk: 'Low' as const,
        leverageRatio: 0.65,
        debtServiceCoverage: 0,
        volatility: 12.5
      };
    }
    const avgOccupancy = metrics.avgOccupancy;
    const portfolioValue = portfolio.reduce((sum, p) => sum + (p.noi * 180), 0);
    const totalNOI = portfolio.reduce((sum, p) => sum + p.noi, 0);

    return {
      concentrationRisk: portfolio.length < 3 ? 'High' : portfolio.length < 5 ? 'Medium' : 'Low',
      occupancyRisk: avgOccupancy < 85 ? 'High' : avgOccupancy < 90 ? 'Medium' : 'Low',
      leverageRatio: 0.65, // Mock leverage
      debtServiceCoverage: portfolioValue > 0 ? (totalNOI * 12) / (portfolioValue * 0.65 * 0.05) : 0, // Mock DSCR
      volatility: 12.5 // Mock volatility percentage
    };
  }, [portfolio, metrics]);

  // Property-level performance
  const propertyPerformance = useMemo(() => {
    if (!portfolio || portfolio.length === 0) return [];
    return portfolio.map(p => ({
      name: p.name,
      noi: p.noi,
      occupancy: p.occupancyRate,
      value: p.noi * 180,
      roi: ((p.noi * 12) / (p.noi * 180 * 0.2)) * 100,
      expenses: p.noi * 0.4 // Mock expenses
    }));
  }, [portfolio]);

  // Performance trend data for overview tab
  const performanceTrendData = useMemo(() => {
    // Generate mock monthly performance data
    const months = [];
    const now = getAppNow();
    const roi = metrics?.roi ?? 0;
    const ytd = distributions?.ytd ?? 0;
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const getMonthName = (date: Date) => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthNames[date.getMonth()];
      };
      // Use deterministic values based on month index instead of Math.random()
      const seed = i * 13 + date.getMonth();
      months.push({
        month: getMonthName(date),
        roi: roi + ((seed % 20 - 10) / 10), // Mock variation using deterministic seed
        distributions: (ytd / 12) * (12 - i) / 12
      });
    }
    return months;
  }, [metrics?.roi, distributions?.ytd]);

  const navigate = useNavigate();
  const navigateTo = useNavigateTo();

  // Early return if no investor ID or metrics not ready - AFTER all hooks
  if (!currentInvestorId || isLoading || !metricsData) {
    return <div className="p-8 text-center text-slate-500">
      {currentInvestorId ? 'Loading investor profile...' : 'No investor ID found. Please log in.'}
    </div>;
  }

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Welcome back, Investor</h1>
          <p className="text-slate-500 dark:text-slate-400">Your investment performance overview.</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigateTo('/app/investors/communication')}
            className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition flex items-center"
          >
            <MessageSquare size={16} className="mr-2" /> Messages
          </button>
          <button
            onClick={() => navigateTo('/app/investors/documents')}
            className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition flex items-center"
          >
            <FileText size={16} className="mr-2" /> Documents
          </button>
          <button
            onClick={() => navigateTo('/app/investors/reports')}
            className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition flex items-center"
          >
            <BarChart3 size={16} className="mr-2" /> Reports
          </button>
          <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-bold border border-emerald-200 dark:border-emerald-800 flex items-center">
            <ShieldCheck size={14} className="mr-1" /> Verified Investor
          </span>
        </div>
      </div>

      {/* Financial Hero Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:shadow-md active:scale-[0.98] active:shadow-inner cursor-default">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Invested</span>
            <DollarSign size={20} className="text-slate-400" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(metrics.totalCapitalInvested / 1000, 0)}k</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Across {entities.length} {entities.length === 1 ? 'Entity' : 'Entities'}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:shadow-md active:scale-[0.98] active:shadow-inner cursor-default">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Est. ROI (Annual)</span>
            <TrendingUp size={20} className="text-brand-500" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatPercentage(metrics.roi, 1)}</div>
          <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center mt-1">
            <ArrowUpRight size={12} className="mr-1" /> Performing well
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:shadow-md active:scale-[0.98] active:shadow-inner cursor-default">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Distributions (YTD)</span>
            <DollarSign size={20} className="text-emerald-500" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(distributions.ytd / 1000, 1)}k</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Next payout: Nov 15
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:shadow-md active:scale-[0.98] active:shadow-inner cursor-default">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Portfolio Health</span>
            <ShieldCheck size={20} className={metrics.avgOccupancy > 90 ? "text-emerald-500" : "text-amber-500"} />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.avgOccupancy}% Occ.</div>
          <div className={`text-xs mt-1 ${metrics.avgOccupancy > 90 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {metrics.avgOccupancy > 90 ? 'High Stability' : 'Action Required'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Allocation */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <h3 className="font-bold text-slate-900 dark:text-white mb-4">Entity Allocation</h3>
          <div className="w-full h-[250px] min-w-0">
            {allocationData.length > 0 ? (
              <ResponsiveContainer width="99%" height="100%" minWidth={0}>
                <RePieChart>
                  <Pie
                    data={allocationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {allocationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: tooltipBg, borderRadius: '12px', border: tooltipBorder, color: tooltipColor }}
                    itemStyle={{ color: tooltipColor }}
                  />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">No active allocations</div>
            )}
          </div>
          <div className="flex flex-wrap gap-4 justify-center mt-4">
            {allocationData.map((entry, index) => (
              <div key={index} className="flex items-center text-xs text-slate-600 dark:text-slate-300">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                {entry.name}
              </div>
            ))}
          </div>
        </div>

        {/* Distribution History & Performance */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900 dark:text-white">Distribution History</h3>
            <button
              onClick={() => navigateTo('/app/investments/distributions')}
              className="text-xs font-bold text-brand-600 hover:underline"
            >
              View All
            </button>
          </div>

          <div className="flex-1 space-y-4">
            {/* Mock distribution history - in real app, fetch from API */}
            <div className="flex items-center p-3 border border-slate-100 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 mr-3">
                <DollarSign size={18} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate-900 dark:text-white">Q3 Distribution</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Oct 15, 2023</div>
              </div>
              <div className="font-mono font-bold text-emerald-600 dark:text-emerald-400">+{formatCurrency(distributions.ytd / 4)}</div>
            </div>
            <div className="flex items-center p-3 border border-slate-100 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 mr-3">
                <FileText size={18} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate-900 dark:text-white">K-1 Tax Form Available</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Aug 20, 2023</div>
              </div>
              <button className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center">
                <Download size={14} className="mr-1" /> Download
              </button>
            </div>
            <div className="flex items-center p-3 border border-slate-100 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 mr-3">
                <DollarSign size={18} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate-900 dark:text-white">Q2 Distribution</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Jul 15, 2023</div>
              </div>
              <div className="font-mono font-bold text-emerald-600 dark:text-emerald-400">+{formatCurrency(distributions.ytd / 4)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Tabs */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex border-b border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setAnalyticsView('overview')}
            className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition ${analyticsView === 'overview'
              ? 'border-brand-600 text-brand-600 dark:border-brand-400 dark:text-brand-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
          >
            Overview
          </button>
          <button
            onClick={() => setAnalyticsView('performance')}
            className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition ${analyticsView === 'performance'
              ? 'border-brand-600 text-brand-600 dark:border-brand-400 dark:text-brand-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
          >
            Performance Analysis
          </button>
          <button
            onClick={() => setAnalyticsView('risk')}
            className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition ${analyticsView === 'risk'
              ? 'border-brand-600 text-brand-600 dark:border-brand-400 dark:text-brand-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
          >
            Risk Analysis
          </button>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {analyticsView === 'overview' && (
            <div className="space-y-6">
              {/* Performance Trend Chart */}
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-4">Performance Trend (Last 12 Months)</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceTrendData}>
                      <defs>
                        <linearGradient id="colorROI" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.brand500} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={chartColors.brand500} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} opacity={0.1} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: tooltipBg, border: tooltipBorder, borderRadius: '12px', color: tooltipColor }}
                        itemStyle={{ color: tooltipColor }}
                        formatter={(value: unknown, name: string) => {
                          if (name === 'roi') return formatPercentage(Number(value), 1);
                          return formatCurrency(Number(value), { showCents: false });
                        }}
                      />
                      <Area type="monotone" dataKey="roi" stroke={chartColors.brand500} fillOpacity={1} fill="url(#colorROI)" />
                      <Area type="monotone" dataKey="distributions" stroke={chartColors.emerald500} fillOpacity={0.3} fill={chartColors.emerald500} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Performance Analysis Tab */}
          {analyticsView === 'performance' && (
            <div className="space-y-6">
              {/* Portfolio vs Benchmark */}
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-4">Portfolio vs Market Benchmark</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceComparison}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} opacity={0.1} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: tooltipBg, borderRadius: '12px', border: tooltipBorder, color: tooltipColor }}
                        formatter={(value: unknown) => formatPercentage(Number(value), 1)}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="portfolio" stroke={chartColors.brand500} strokeWidth={2.5} name="Your Portfolio" />
                      <Line type="monotone" dataKey="benchmark" stroke={chartColors.slate400} strokeWidth={2} strokeDasharray="5 5" name="Market Benchmark" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ROI Scenarios */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {roiScenarios.map((scenario, index) => (
                  <div key={index} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{scenario.scenario}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{scenario.probability}%</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatPercentage(scenario.roi, 1)}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Projected ROI</div>
                  </div>
                ))}
              </div>

              {/* Property-Level Performance */}
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-4">Property-Level Performance</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={propertyPerformance}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} opacity={0.1} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: tooltipBg, borderRadius: '12px', border: tooltipBorder, color: tooltipColor }}
                      />
                      <Legend />
                      <Bar dataKey="roi" fill={chartColors.brand500} name="ROI %" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="occupancy" fill={chartColors.emerald500} name="Occupancy %" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Risk Analysis Tab */}
          {analyticsView === 'risk' && (
            <div className="space-y-6">
              {/* Risk Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Concentration Risk</span>
                    <AlertTriangle size={16} className={riskMetrics.concentrationRisk === 'High' ? 'text-red-500' : riskMetrics.concentrationRisk === 'Medium' ? 'text-amber-500' : 'text-emerald-500'} />
                  </div>
                  <div className={`text-2xl font-bold ${riskMetrics.concentrationRisk === 'High' ? 'text-red-600 dark:text-red-400' :
                    riskMetrics.concentrationRisk === 'Medium' ? 'text-amber-600 dark:text-amber-400' :
                      'text-emerald-600 dark:text-emerald-400'
                    }`}>
                    {riskMetrics.concentrationRisk}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{portfolio.length} properties</div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Occupancy Risk</span>
                    <ShieldCheck size={16} className={riskMetrics.occupancyRisk === 'High' ? 'text-red-500' : riskMetrics.occupancyRisk === 'Medium' ? 'text-amber-500' : 'text-emerald-500'} />
                  </div>
                  <div className={`text-2xl font-bold ${riskMetrics.occupancyRisk === 'High' ? 'text-red-600 dark:text-red-400' :
                    riskMetrics.occupancyRisk === 'Medium' ? 'text-amber-600 dark:text-amber-400' :
                      'text-emerald-600 dark:text-emerald-400'
                    }`}>
                    {riskMetrics.occupancyRisk}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{metrics.avgOccupancy}% avg occupancy</div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Leverage Ratio</span>
                    <Activity size={16} className="text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatPercentage(riskMetrics.leverageRatio * 100, 0)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Loan to value</div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">DSCR</span>
                    <TrendingUp size={16} className="text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(riskMetrics.debtServiceCoverage, 2)}x</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Debt service coverage</div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Volatility</span>
                    <TrendingDown size={16} className="text-amber-500" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatPercentage(riskMetrics.volatility, 1)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Annual volatility</div>
                </div>
              </div>

              {/* Risk Summary */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">Risk Assessment</p>
                    <p className="text-blue-800 dark:text-blue-300">
                      Your portfolio shows {riskMetrics.concentrationRisk.toLowerCase()} concentration risk with {portfolio.length} properties.
                      {riskMetrics.occupancyRisk === 'High' && ' Occupancy levels are below optimal. Consider diversifying your portfolio.'}
                      {riskMetrics.occupancyRisk !== 'High' && ' Overall portfolio health is good.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entity Breakdown Section */}
      {entities.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <span className="font-bold text-slate-900 dark:text-white">Investment Entities</span>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entityPerformance.map((ep) => (
                <div key={ep.entity.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">{ep.entity.name}</h4>
                    <span className="text-xs px-2 py-1 bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 rounded">
                      {ep.entity.type}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Ownership:</span>
                      <span className="text-slate-900 dark:text-white font-medium">{ep.ownershipPercentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Capital Invested:</span>
                      <span className="text-slate-900 dark:text-white font-medium">{formatNumber(ep.capitalInvested / 1000, 0)}k</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Properties:</span>
                      <span className="text-slate-900 dark:text-white font-medium">{ep.properties.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Distributions YTD:</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-medium">{formatNumber(ep.distributions / 1000, 1)}k</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* My Investments Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <span className="font-bold text-slate-900 dark:text-white">My Active Investments</span>
          <div className="flex gap-2">
            <button
              onClick={() => navigateTo('/app/investments/portfolio')}
              className="text-sm font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center"
            >
              <Eye size={16} className="mr-1" /> View Portfolio
            </button>
            <button
              onClick={() => navigateTo('/app/investments')}
              className="text-sm font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center"
            >
              Browse Programs
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">Entity</th>
                <th className="px-6 py-4">Property</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Units</th>
                <th className="px-6 py-4">Occupancy</th>
                <th className="px-6 py-4">NOI (Mo)</th>
                <th className="px-6 py-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {portfolio.map((prop) => {
                // Find which entity owns this property
                const owningEntity = entities.find(e => e.propertyIds.includes(prop.id));
                const entityInvestment = entityPerformance.find(ep => ep.entity.id === owningEntity?.id);

                return (
                  <tr key={prop.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">{owningEntity?.name || 'Unknown'}</div>
                      {entityInvestment && (
                        <div className="text-xs text-slate-500">{entityInvestment.ownershipPercentage}% ownership</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-900 dark:text-white">{prop.name}</div>
                      <div className="text-xs text-slate-500">{prop.address}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {prop.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {prop.units}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 mr-2">
                          <div className="bg-brand-500 h-1.5 rounded-full" style={{ width: `${prop.occupancyRate}%` }}></div>
                        </div>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{prop.occupancyRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-emerald-600 dark:text-emerald-400 font-bold">
                      {formatNumber(prop.noi / 1000, 1)}k
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate(`/app/investors/properties/${prop.id}`)}
                        className="text-brand-600 dark:text-brand-400 font-bold text-xs hover:underline"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {portfolio.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">No active investments found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

